/* eslint-disable @typescript-eslint/no-explicit-any */
import { Capacitor } from "@capacitor/core";
import useAuth from "../hooks/useAuth";
import { apiClient } from "./api";
import { Preferences } from "@capacitor/preferences";

/**
 * Fetch all goals with optional filtering
 * @param params Query parameters for filtering goals
 * @returns List of goals and their count
 */
export const fetchGoals = async (params: {
  profile_id?: number;
  goal_type?: string;
  isActive?: boolean;
  effective_date?: string;
}): Promise<{ goals: any[]; count: number }> => {
  try {
    const response = await apiClient.get("/api/user_goals", { params });
    await Preferences.set({
      key: "goals",
      value: JSON.stringify(response.data.goals),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching goals:", error);
    throw error;
  }
};

/**
 * Fetch a specific goal by ID
 * @param goalId ID of the goal to fetch
 * @returns Goal data
 */
export const fetchGoalById = async (goalId: number): Promise<any> => {
  try {
    const response = await apiClient.get(`/api/user_goals/${goalId}`);
    return response.data.goal;
  } catch (error) {
    console.error("Error fetching goal by ID:", error);
    throw error;
  }
};

/**
 * Create a new therapy goal
 * @param goalData Data for the new goal
 * @returns Created goal data
 */
export const createGoal = async (goalData: {
  goal_id?: number;
  profile_id: number;
  device_id?: string;
  goal_type: string;
  target_count?: number;
  target_seconds?: number;
  target_hours?: number;
  measurement_unit: string;
  days_of_week: string[];
  effective_from: string;
  effective_to?: string;
}): Promise<any> => {
  try {
    const response = await apiClient.post("/api/user_goals", goalData);
    return response.data.goal;
  } catch (error) {
    console.error("Error creating goal:", error);
    throw error;
  }
};

/**
 * Update an existing goal
 * @param goalId ID of the goal to update
 * @param goalData Updated goal data
 * @returns Updated goal data
 */
export const updateGoal = async (
  goalId: number,
  goalData: Partial<{
    device_id: string;
    goal_type: string;
    target_count: number;
    target_seconds: number;
    target_hours: number;
    measurement_unit: string;
    days_of_week: string[];
    effective_from: string;
    effective_to: string;
    isActive: boolean;
  }>
): Promise<any> => {
  try {
    const response = await apiClient.put(`/api/user_goals/${goalId}`, goalData);
    return response.data.goal;
  } catch (error) {
    console.error("Error updating goal:", error);
    throw error;
  }
};

/**
 * Deactivate a goal by setting isActive to false
 * @param goalId ID of the goal to deactivate
 * @returns Deactivated goal data
 */
export const deactivateGoal = async (goalId: number): Promise<any> => {
  try {
    const response = await apiClient.put(
      `/api/user_goals/${goalId}/deactivate`
    );
    return response.data.goal;
  } catch (error) {
    console.error("Error deactivating goal:", error);
    throw error;
  }
};

/**
 * Fetch active goals for a specific user profile
 * @param profileId Profile ID to fetch active goals for
 * @returns List of active goals
 */
export const fetchActiveGoalsByProfile = async (
  profileId: number
): Promise<any[]> => {
  try {
    const response = await apiClient.get(`/api/user_goals/active/${profileId}`);
    return response.data.goals;
  } catch (error) {
    console.error("Error fetching active goals by profile:", error);
    throw error;
  }
};

/**
 * Fetch goals for a specific therapy type and user profile
 * @param therapyType Therapy type to filter goals
 * @param profileId Profile ID to fetch goals for
 * @returns List of goals for the specified therapy type and profile
 */
export const fetchGoalsByTherapyAndProfile = async (
  therapyType: string,
  profileId: number
): Promise<any[]> => {
  try {
    const response = await apiClient.get(
      `/api/user_goals/therapy/${therapyType}/profile/${profileId}`
    );
    return response.data.goals;
  } catch (error) {
    console.error("Error fetching goals by therapy and profile:", error);
    throw error;
  }
};

/**
 * Fetch goal history for a user
 * @param profileId Profile ID to fetch goal history for
 * @param params Query parameters for filtering goal history
 * @returns List of goals in the user's history
 */
export const fetchGoalHistory = async (
  profileId: number,
  params: {
    from_date?: string;
    to_date?: string;
    goal_type?: string;
  }
): Promise<any[]> => {
  try {
    const response = await apiClient.get(
      `/api/user_goals/history/${profileId}`,
      {
        params,
      }
    );
    return response.data.goals;
  } catch (error) {
    console.error("Error fetching goal history:", error);
    throw error;
  }
};

/**
 * Fetch upcoming goals for a user
 * @param profileId Profile ID to fetch upcoming goals for
 * @returns List of upcoming goals
 */
export const fetchUpcomingGoals = async (profileId: number): Promise<any[]> => {
  try {
    const response = await apiClient.get(
      `/api/user_goals/upcoming/${profileId}`
    );
    return response.data.goals;
  } catch (error) {
    console.error("Error fetching upcoming goals:", error);
    throw error;
  }
};

/**
 * Check if goals exist for specific criteria
 * @param params Query parameters to check for goals
 * @returns Boolean indicating if goals exist
 */
export const checkGoalsExist = async (params: {
  profile_id: number;
  goal_type?: string;
  effective_date?: string;
}): Promise<boolean> => {
  try {
    const response = await apiClient.get("/api/user_goals/check", { params });
    return response.data.exists;
  } catch (error) {
    console.error("Error checking if goals exist:", error);
    throw error;
  }
};

/**
 * Copy goals from one profile to another
 * @param sourceProfileId Source profile ID
 * @param targetProfileId Target profile ID
 * @param therapyTypes List of therapy types to copy
 * @returns List of copied goals
 */
export const copyGoalsToProfile = async (
  sourceProfileId: number,
  targetProfileId: number,
  therapyTypes?: string[]
): Promise<any[]> => {
  try {
    const response = await apiClient.post("/api/user_goals/copy", {
      sourceProfileId,
      targetProfileId,
      therapyTypes,
    });
    return response.data.goals;
  } catch (error) {
    console.error("Error copying goals to profile:", error);
    throw error;
  }
};
export const setDefaultGoalsForProfile = async (
  goal_types?: string[]
): Promise<any[]> => {
  try {
    const response = await apiClient.post(
      "/api/user_goals/set_defaults", {
        goal_types,
      }
    );
    return response.data.goals;
  } catch (error) {
    console.error("Error setting default goals for profile:", error);
    throw error;
  }
};